/**
 * Per-lens legend bar — UX2 + UX3 of the 2026-06-01 holistic mesh redesign.
 *
 * Each overlay answers a different question. The legend bar makes that
 * question + the visual mapping explicit. Without this, switching
 * between lenses looks like changing nothing (especially when data is
 * sparse — no findings, no drift, no compliance framework).
 *
 * Empty-state messaging is integrated: if the active overlay has no
 * useful signal in the current mesh (e.g., Drift on a single-scan
 * view), we replace the legend with an explicit "why this is empty"
 * line so users don't think the feature is broken.
 */
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { NODE_TYPE_KEY, legendEntryFor } from './nodeRenderers'
import type { MeshResponse, OverlayMode } from './types'

interface LensLegendProps {
  overlay: OverlayMode
  mesh: MeshResponse
  scope: 'analysis' | 'tenant'
  complianceFramework?: string | null
  availableFrameworks?: Array<{ key: string; display_name?: string }>
  /** The legend entry being emphasised, as `type` or `type:componentKind`. */
  emphasis?: string | null
  onEmphasisChange?: (key: string | null) => void
}

/**
 * Per-lens headline question. Anchors the user's mental model: "the
 * question THIS lens is built to answer."
 */
const LENS_HEADLINE: Record<OverlayMode, string> = {
  risk: 'Where do security findings concentrate?',
  blast_radius: 'If this misbehaves — or is wrong — what is affected?',
  drift: 'What has changed recently?',
  compliance: 'Which agents fail this framework\'s controls?',
  framework: 'What\'s the tech-stack breakdown?',
}

/**
 * Empty-state predicate per lens — returns a message when the lens
 * has no useful signal in the current mesh, else null.
 */
function detectEmptyState(
  overlay: OverlayMode,
  mesh: MeshResponse,
  scope: 'analysis' | 'tenant',
  complianceFramework: string | null | undefined,
): string | null {
  const agentNodes = mesh.nodes.filter((n) => n.type === 'agent')
  if (agentNodes.length === 0) {
    return 'No agents detected in this scope.'
  }

  switch (overlay) {
    case 'risk': {
      const withFindings = agentNodes.filter(
        (n) => n.findings_count > 0 || n.affected_findings_count > 0,
      )
      if (withFindings.length === 0) {
        return `All clear — 0 findings on the ${agentNodes.length} agent${agentNodes.length === 1 ? '' : 's'} in scope.`
      }
      return null
    }
    case 'blast_radius': {
      const withDownstream = agentNodes.filter((n) => (n.blast_radius ?? n.downstream_count ?? 0) > 0)
      if (withDownstream.length === 0) {
        return 'No outbound dependencies detected — every agent is an isolated leaf.'
      }
      const counts = agentNodes.map((n) => n.blast_radius ?? n.downstream_count ?? 0)
      const max = Math.max(...counts)
      const min = Math.min(...counts.filter((c) => c > 0))
      if (max === min) {
        return `All agents have ${max} downstream dependencies (likely a workspace-wide MCP/A2A config). Blast Radius can't distinguish them.`
      }
      return null
    }
    case 'drift': {
      const drifted = agentNodes.filter((n) => n.drift_recent === true)
      if (scope === 'analysis' && drifted.length === 0) {
        return 'This is a single scan — drift requires scan history. Visit the Topology page once you have multiple scans for the same repo.'
      }
      if (drifted.length === 0) {
        return 'No agents have changed their behavioral fingerprint in the last 30 days. Your platform is stable.'
      }
      return null
    }
    case 'compliance': {
      if (!complianceFramework) {
        return 'Pick a framework above to compute per-agent compliance status.'
      }
      const haveStatus = agentNodes.filter(
        (n) => n.compliance_status && n.compliance_status !== 'not_checked',
      )
      if (haveStatus.length === 0) {
        return `No SCF controls in this framework map to detected rule_ids on these agents. Either the framework has incomplete crosswalk data or your findings don't touch its controls.`
      }
      return null
    }
    case 'framework': {
      const frameworks = new Set(
        agentNodes.map((n) => n.framework).filter(Boolean),
      )
      if (frameworks.size <= 1) {
        return frameworks.size === 1
          ? `All agents are on the same framework (${[...frameworks][0]}). Try the Risk or Blast Radius lens for differentiation.`
          : 'No framework metadata on any agent.'
      }
      return null
    }
    default:
      return null
  }
}

/**
 * Per-lens visual key — color swatches, size scaling, etc. Tells the
 * user what they're looking at when the lens is active.
 */
function LegendKey({
  overlay,
  complianceFramework,
}: {
  overlay: OverlayMode
  complianceFramework?: string | null
}) {
  const swatch = (color: string, label: string, dashed = false) => (
    <Stack key={label} direction="row" spacing={0.5} alignItems="center">
      <Box
        sx={{
          width: 14,
          height: 14,
          bgcolor: color,
          border: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
          borderRadius: 0.5,
        }}
      />
      <Typography variant="caption" sx={{ fontSize: 11 }}>
        {label}
      </Typography>
    </Stack>
  )
  switch (overlay) {
    case 'risk':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            Border color = max finding severity:
          </Typography>
          {swatch('#d32f2f', 'critical')}
          {swatch('#ed6c02', 'high')}
          {swatch('#f9a825', 'medium')}
          {swatch('#0288d1', 'low')}
          {swatch('#cfd8dc', 'no findings')}
        </Stack>
      )
    case 'blast_radius':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            Width scales with what an agent reaches, or what depends on a store:
          </Typography>
          {swatch('#cfd8dc', '0')}
          {swatch('#90a4ae', '1')}
          {swatch('#0288d1', '2-3')}
          {swatch('#7b1fa2', '4-7')}
          {swatch('#d32f2f', '8+')}
        </Stack>
      )
    case 'drift':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            Border = verdict (last 30 days):
          </Typography>
          {swatch('#d32f2f', 'critical')}
          {swatch('#fb8c00', 'material')}
          {swatch('#ffc107', 'cosmetic')}
          {swatch('#388e3c', 'improved')}
          {swatch('#1976d2', 'rebaseline')}
          {swatch('#e0e0e0', 'stable (dimmed)')}
        </Stack>
      )
    case 'compliance':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            {complianceFramework
              ? `Border color = per-agent ${complianceFramework} status:`
              : 'Pick a framework above to populate:'}
          </Typography>
          {swatch('#388e3c', 'aligned')}
          {swatch('#fb8c00', 'partial')}
          {swatch('#d32f2f', 'gap')}
          {swatch('#bdbdbd', 'not checked')}
        </Stack>
      )
    case 'framework':
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            Border color = framework label:
          </Typography>
          {swatch('#1976d2', 'langchain')}
          {swatch('#0288d1', 'langgraph')}
          {swatch('#7b1fa2', 'crewai')}
          {swatch('#388e3c', 'autogen')}
          {swatch('#00897b', 'openai')}
          {swatch('#3f51b5', 'claude code')}
          {swatch('#90a4ae', 'other')}
        </Stack>
      )
    default:
      return null
  }
}

/**
 * What each box on the canvas *is*.
 *
 * Separate from the lens key and shown on every lens, because "which of these
 * is an agent and which is a store" is not a question about the active
 * overlay — it is the question a reader has before any lens means anything.
 * Colour on this canvas already carries the lens (severity under Risk,
 * framework under Framework), so type is carried by shape, and until now that
 * mapping was stated once in prose on one lens and drawn nowhere.
 *
 * Only the types actually present are listed. A key naming seven kinds of
 * node for a mesh containing two is a wall of irrelevance, and it teaches a
 * reader to stop reading the key.
 */
function NodeTypeKey({
  mesh, emphasis, onEmphasisChange,
}: {
  mesh: MeshResponse
  emphasis?: string | null
  onEmphasisChange?: (key: string | null) => void
}) {
  // Counted rather than merely listed. The commonest question is not "where
  // is it" but "do we have any at all", and a legend that answers that
  // without a hunt answers it in the place the reader is already looking.
  const counts = new Map<string, number>()
  for (const node of mesh.nodes) {
    const entry = legendEntryFor(node)
    if (!entry) continue
    const key = entry.componentKind ? `${entry.type}:${entry.componentKind}` : entry.type
    counts.set(key, (counts.get(key) || 0) + 1)
  }
  const entries = NODE_TYPE_KEY
    .map((e) => ({
      ...e,
      key: e.componentKind ? `${e.type}:${e.componentKind}` : e.type,
    }))
    .filter((e) => (counts.get(e.key) || 0) > 0)
  if (entries.length < 2) return null

  const clickable = !!onEmphasisChange
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
      <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
        {clickable ? 'Click a shape to pick it out:' : 'Shape says what a node is:'}
      </Typography>
      {entries.map((entry) => {
        const active = emphasis === entry.key
        return (
          <Tooltip
            key={entry.key}
            title={clickable
              ? (active ? 'Show everything again' : `Dim everything except ${entry.label}`)
              : entry.label}
            placement="top"
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={clickable
                ? () => onEmphasisChange?.(active ? null : entry.key)
                : undefined}
              sx={{
                cursor: clickable ? 'pointer' : 'default',
                px: 0.5,
                borderRadius: 0.5,
                // Emphasis is shown ON the legend too, so the canvas state
                // has a visible cause. A dimmed graph with nothing explaining
                // why reads as a rendering fault.
                bgcolor: active ? 'action.selected' : 'transparent',
                opacity: emphasis && !active ? 0.45 : 1,
                '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 11,
                  border: `2px solid ${entry.color}`,
                  // The drum every diagramming convention has used for a
                  // datastore since the 1970s, approximated at legend size: a
                  // rounded top and bottom rather than a rectangle.
                  borderRadius: entry.shape === 'store' ? '6px / 4px' : 0.5,
                  bgcolor: 'background.paper',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                {entry.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: 10, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
              >
                {counts.get(entry.key)}
              </Typography>
            </Stack>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export default function LensLegend({
  overlay,
  mesh,
  scope,
  complianceFramework,
  availableFrameworks: _availableFrameworks,
  emphasis,
  onEmphasisChange,
}: LensLegendProps) {
  const emptyMessage = detectEmptyState(overlay, mesh, scope, complianceFramework)
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: emptyMessage ? 'warning.50' : 'grey.50',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Tooltip title="The question this lens is built to answer" placement="top">
          <Chip
            size="small"
            label={LENS_HEADLINE[overlay]}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            }}
          />
        </Tooltip>
        {emptyMessage ? (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'warning.dark' }}>
            {emptyMessage}
          </Typography>
        ) : (
          <LegendKey overlay={overlay} complianceFramework={complianceFramework} />
        )}
      </Stack>
      {/* Shown even when the lens has no signal to report: a reader still has
          to be able to tell what the boxes are. */}
      <NodeTypeKey mesh={mesh} emphasis={emphasis} onEmphasisChange={onEmphasisChange} />
    </Box>
  )
}
